import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authAPI } from '../services/api.js';
import Login from './Login.jsx';
import logo from '../src/assests/wealth_plus.png';
import ForgotPassword from './ForgotPassword.jsx';
import SuperAdminDashboard from './SuperAdminDashboard.jsx';
import LeaderDashboard from './LeaderDashboard.jsx';
import AdvisorDashboard from './AdvisorDashboard.jsx';
import HealthPlusDashboard from './HealthPlusDashboard.jsx';


const App = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);


  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        const user = await authAPI.getCurrentUser();
        if (mounted) {
          // Only update state if component is still mounted
          setCurrentUser(user);
          setLoading(false);
        }
      } catch (error) {
        if (mounted) {
          // Handle different error scenarios
          if (error.message === 'Network error - please check your connection') {
            console.error('Network Error: Unable to connect to server');
          } else {
            console.error('Auth check failed:', error.message);
          }
          setCurrentUser(null);
          setLoading(false);
        }
      }
    };

    // Add a delay to throttle authentication checks and prevent rate limiting
    const timeoutId = setTimeout(() => {
      checkAuth();
    }, 1000);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, []);

  // Registration / self-signup is disabled in production. Only super admins may create users.

  const handleLogin = (user) => {
    setCurrentUser(user);
  };

  const handleRegister = (user) => {
    setCurrentUser(user);
  };

  const handleSwitchToForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
  };

  const handleLogout = () => {
    // Clear cookies
    document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    setCurrentUser(null);
  };

  const fetchUser = async () => {
    try {
      const user = await authAPI.getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const renderDashboard = () => {
    if (!currentUser) {
      if (showForgotPassword) {
        return <ForgotPassword onBackToLogin={handleBackToLogin} />;
      }
      return <Login
        onLogin={handleLogin}
        onSwitchToForgotPassword={handleSwitchToForgotPassword}
      />;
    }

    const dashboardProps = {
      user: currentUser,
      refreshUser: fetchUser
    };

    switch (currentUser.role) {
      case 'super_admin':
        return <SuperAdminDashboard {...dashboardProps} />;
      case 'leader':
        return <LeaderDashboard {...dashboardProps} />;
      case 'advisor':
        return <AdvisorDashboard {...dashboardProps} />;
      case 'health_plus_partner':
        return <HealthPlusDashboard {...dashboardProps} />;
      default:
        return (
          <div className="p-8 text-center">
            <div className="text-red-500 mb-4">Invalid user role: {currentUser.role}</div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Logout and Login Again
            </button>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50 text-gray-800">
        {currentUser && (
          <header className="bg-white shadow-md">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center py-4">
                <div className="flex items-center">
                  <h1 className="text-2xl font-bold "><span className="text-[#325585]" >WEALTH</span> <span className="text-[#021734] "> PLUS </span>Acadmey</h1>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">Welcome, {currentUser.name}</span>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </header>
        )}
        <main className="container mx-auto sm:p-6 lg:p-8">
          <Routes>
            <Route path="/login" element={
              showForgotPassword ? <ForgotPassword onBackToLogin={handleBackToLogin} /> : <Login onLogin={handleLogin} onSwitchToForgotPassword={handleSwitchToForgotPassword} />
            } />
            <Route path="/dashboard" element={
              currentUser ? renderDashboard() : <Navigate to="/login" replace />
            } />
            <Route path="*" element={<Navigate to={currentUser ? "/dashboard" : "/login"} replace />} />
          </Routes>
        </main>



        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </Router>
  );
};

export default App;